import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowDown, Check, CircleCheck, Phone } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button'
import { Container } from '@/components/generals/container'
import { StarRating } from '@/components/generals/star-rating'
import { formatEuro } from '@/lib/format'
import { computeEstimate } from '@/lib/pricing'

import { garage } from '@/constants/garage'

const HIGHLIGHT = 'underline decoration-primary decoration-[0.1em] underline-offset-[0.13em] [text-decoration-skip-ink:none]'

const TRUST_POINTS = [
    'Toutes marques, même sous garantie constructeur',
    'Rendez-vous sous 48\u00A0h',
    'Véhicule de prêt sur réservation'
]

// Exemple réel tiré de la grille : compacte, révision et plaquettes avant.
const sample = computeEstimate('compacte', ['revision', 'plaquettes'])

const RepairOrderCard: FC = (): ReactNode => {
    return (
        <div className='relative mx-4 -mt-16 rounded-lg border border-border bg-card p-5 shadow-float sm:mx-auto sm:max-w-[24rem] lg:absolute lg:-left-10 lg:bottom-10 lg:mx-0 lg:mt-0 lg:w-[22.5rem] xl:-left-14'>
            <div className='flex items-center justify-between gap-4'>
                <p className='label-mono text-muted-foreground'>
                    Ordre de réparation
                </p>

                <p className='label-mono text-foreground'>
                    N° 2847
                </p>
            </div>

            <p className='mt-3 font-semibold leading-snug'>
                Peugeot 308 · révision et plaquettes avant
            </p>

            <dl className='mt-4 space-y-2 font-mono text-sm'>
                <div className='flex items-baseline gap-2'>
                    <dt className='text-muted-foreground'>Annoncé au devis</dt>
                    <span aria-hidden='true' className='flex-1 border-b border-dotted border-foreground/25' />
                    <dd className='tabular-nums'>{formatEuro(sample.totalTtc, true)}</dd>
                </div>

                <div className='flex items-baseline gap-2'>
                    <dt className='text-muted-foreground'>Facturé</dt>
                    <span aria-hidden='true' className='flex-1 border-b border-dotted border-foreground/25' />
                    <dd className='tabular-nums'>{formatEuro(sample.totalTtc, true)}</dd>
                </div>

                <div className='flex items-baseline gap-2'>
                    <dt className='font-semibold'>Écart</dt>
                    <span aria-hidden='true' className='flex-1 border-b border-dotted border-foreground/25' />
                    <dd className='font-semibold tabular-nums text-signal-text'>{formatEuro(0, true)}</dd>
                </div>
            </dl>

            <p className='mt-4 flex items-start gap-2 border-t border-dashed border-border pt-4 text-sm leading-snug'>
                <CircleCheck aria-hidden='true' className='mt-px size-4 shrink-0 text-success' />
                Promise mardi 18&nbsp;h, rendue à 17&nbsp;h&nbsp;20
            </p>
        </div>
    )
}

export const HeroSection: FC = (): ReactNode => {
    return (
        <section id='top' aria-labelledby='hero-title' className='relative'>
            <Container className='grid items-center gap-12 pb-16 pt-8 sm:pt-12 lg:grid-cols-12 lg:gap-10 lg:pb-24 lg:pt-14 xl:gap-16'>
                <div className='lg:col-span-7'>
                    <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
                        <StarRating label={`Note moyenne de ${garage.rating.display} sur 5`} />

                        <p className='text-sm'>
                            <strong className='font-semibold'>{garage.rating.display}/5</strong>
                            <span className='text-muted-foreground'> · {garage.rating.count} avis clients vérifiés</span>
                        </p>
                    </div>

                    <h1 id='hero-title' className='heading mt-5 text-display'>
                        Votre voiture réparée <span className={HIGHLIGHT}>au prix annoncé</span>, rendue <span className={HIGHLIGHT}>le jour promis</span>.
                    </h1>

                    <p className='mt-6 max-w-[36rem] text-lead text-muted-foreground'>
                        Révision, freinage, distribution, pneus&nbsp;: vous connaissez le prix avant qu’on ouvre le capot. Diagnostic offert, devis écrit, et pas un euro de plus sur la facture.
                    </p>

                    <div id='hero-actions' className='mt-8 flex flex-col gap-3 sm:flex-row'>
                        <ButtonLink href={garage.phone.href} size='lg' className='tabular-nums'>
                            <Phone aria-hidden='true' className='size-5' />
                            Appeler le {garage.phone.display}
                        </ButtonLink>

                        <ButtonLink href='#devis' variant='secondary' size='lg'>
                            Estimer mon devis
                            <ArrowDown aria-hidden='true' className='size-5' />
                        </ButtonLink>
                    </div>

                    <ul className='mt-8 flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:gap-x-6'>
                        {TRUST_POINTS.map(point => (
                            <li key={point} className='flex items-center gap-2'>
                                <Check aria-hidden='true' className='size-4 shrink-0 text-foreground' />
                                {point}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='relative lg:col-span-5'>
                    <div className='relative aspect-[4/3] overflow-hidden rounded-xl bg-surface sm:aspect-[16/10] lg:aspect-[4/5]'>
                        <Image
                            src='/images/hero-atelier.jpg'
                            alt='Mécanicien du Garage Kermarec contrôlant un disque de frein neuf sur une voiture levée au pont'
                            fill
                            preload
                            sizes='(min-width: 1440px) 560px, (min-width: 1024px) 40vw, 100vw'
                            className='object-cover object-[38%_60%]'
                        />
                    </div>

                    <RepairOrderCard />
                </div>
            </Container>
        </section>
    )
}
